import { useState, useEffect } from 'react';
import { usePosts } from './usePosts';
import { useSources } from './useSources';
import { useWatchTopics } from './useWatchTopics';
import { useChannels } from './useChannels';
import { useSettings } from './useSettings';
import { dataService } from '../services/dataService';
import { channelsService } from '../services/channelsService';
import { postsService } from '../services/postsService';
import { sourcesService } from '../services/sourcesService';
import { watchTopicsService } from '../services/watchTopicsService';
import { useAuth } from './useAuth';
import { Logger } from '../utils/logger';
import { splitLoadedPosts } from '../utils/postLists';
import type { Post } from '../types';

export function useRaisoData() {
  const { currentSpace, isGuest } = useAuth();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [allDatedPosts, setAllDatedPosts] = useState<import('../types').Post[]>([]);
  const {
    posts, setPosts,
    queue, setQueue,
    newsletterPosts, setNewsletterPosts,
    droppedPostForScheduling, setDroppedPostForScheduling,
    isGenerateModalOpen, setIsGenerateModalOpen,
    addToQueue,
    updatePost,
    unschedulePost,
    deletePost,
    generateNewPosts,
    createPost,
    handleCreatePost,
    duplicatePost,
    addToNewsletter,
    removeFromNewsletter,
    scheduleNewsletter,
    publishPostNow
  } = usePosts();

  const {
    sources, setSources,
    addSource,
    updateSource,
    deleteSource
  } = useSources();

  const {
    watchTopics, setWatchTopics,
    enrichingTopicIds,
    addWatchTopic,
    updateWatchTopic,
    deleteWatchTopic
  } = useWatchTopics();

  const {
    communicationChannels, setCommunicationChannels,
    addCommunicationChannel,
    updateCommunicationChannel,
    deleteCommunicationChannel,
    loadChannels
  } = useChannels();

  // Wrap addToQueue to also update allDatedPosts for calendar refresh
  const addToQueueAndUpdateCalendar = async (post: import('../types').Post, scheduledDate?: Date) => {
    await addToQueue(post, scheduledDate, (scheduledPost) => {
      setAllDatedPosts((prev) => {
        const exists = prev.some((p) => p.id === scheduledPost.id);
        if (exists) {
          return prev.map((p) => (p.id === scheduledPost.id ? scheduledPost : p));
        }
        return [...prev, scheduledPost];
      });
    });
  };

  // Wrap updatePost to also sync allDatedPosts for calendar refresh
  const updatePostAndSyncCalendar = async (post: import('../types').Post, scheduledDate?: Date) => {
    await updatePost(post, scheduledDate, (updatedPost) => {
      setAllDatedPosts((prev) => {
        if (updatedPost.scheduledDate) {
          const exists = prev.some((p) => p.id === updatedPost.id);
          if (exists) {
            return prev.map((p) => (p.id === updatedPost.id ? updatedPost : p));
          }
          return [...prev, updatedPost];
        }
        // If scheduledDate was removed, remove from allDatedPosts
        return prev.filter((p) => p.id !== updatedPost.id);
      });
    });
  };

  const unschedulePostAndSyncCalendar = async (postId: string) => {
    await unschedulePost(postId);
    setAllDatedPosts(prev => prev.filter(p => p.id !== postId));
  };

  // Wrap deletePost to also remove from allDatedPosts
  const deletePostAndSyncCalendar = async (postId: string) => {
    await deletePost(postId);
    setAllDatedPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  // Wrap publishPostNow to surface the freshly published post in the calendar
  // (il acquiert une publishedDate) sans attendre un reload. 2026-06-10.
  const publishPostNowAndSyncCalendar = async (postId: string) => {
    const publishedPost = await publishPostNow(postId);
    if (publishedPost && (publishedPost.scheduledDate || publishedPost.publishedDate)) {
      setAllDatedPosts((prev) => {
        const exists = prev.some((p) => p.id === publishedPost.id);
        return exists
          ? prev.map((p) => (p.id === publishedPost.id ? publishedPost : p))
          : [...prev, publishedPost];
      });
    }
    return publishedPost;
  };

  const {
    automationSettings, setAutomationSettings,
    fetchAutomationSettings, updateAutomationSettings,
    systemPrompt, setSystemPrompt,
    isPositioningTemplate,
    sectorWatchEnabled,
    fetchPositioning,
    logoUrl, setLogoUrl,
    newsletterTemplate, setNewsletterTemplate
  } = useSettings();
  
  // Log initial communication channels for debugging
  useEffect(() => {
    Logger.log('Initial communication channels on mount:', communicationChannels);
  }, []);

  // Répartit les posts chargés depuis l'API entre galerie (TOUS les posts — un post
  // programmé reste visible avec son badge), file (programmés futurs) et calendrier
  // (posts planifiés ET publiés, ex. publish-now sans planification préalable —
  // 2026-06-10).
  const applyLoadedPosts = (postsData: Post[]) => {
    const { gallery, queue: queuePosts, dated } = splitLoadedPosts(postsData);
    setPosts(gallery);
    setQueue(queuePosts);
    setAllDatedPosts(dated);
  };

  // Recharge uniquement les posts (sans skeleton). Utilisé après un batch de
  // génération en échec / partiel : le backend peut avoir persisté des posts
  // après l'abandon du front.
  const refreshPosts = async () => {
    if (!currentSpace) return;
    try {
      const postsData = await postsService.getAll(currentSpace.id);
      applyLoadedPosts(postsData ?? []);
    } catch (error) {
      Logger.log('ℹ️ Failed to refresh posts', error);
    }
  };

  // Fonction pour rafraîchir uniquement les sources (utilisé après sync)
  const refreshSources = async () => {
    if (!currentSpace) return;
    try {
      const sourcesData = await sourcesService.getAll(currentSpace.id);
      if (sourcesData) {
        // Merge intelligent : conserver les sources ajoutées localement
        // qui ne sont pas encore retournées par le backend (race condition ES refresh).
        // Fix V4-UI-DEDUP (2026-05-21) : dédupliquer par id (ou url en fallback
        // pour les entrées optimistes "temp-*") — sinon la même source pouvait
        // apparaître plusieurs fois (backend hit + local temp non remplacé).
        setSources(prev => {
          const backendIds = new Set(sourcesData.map(s => s.id));
          const localOnly = prev.filter(s => !backendIds.has(s.id));
          const merged = [...sourcesData, ...localOnly];
          const seen = new Set<string>();
          return merged.filter(s => {
            const key = s.id || s.url || s.title;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      }
    } catch (error) {
      Logger.error('Error refreshing sources:', error);
    }
  };

  // V4-UX-SETUP-EXPRESS-SILENT-REFRESH — `silent` recharge les données en arrière-plan
  // SANS déclencher `setIsDataLoading(true)`. Sans ça, un refresh en fin de run de
  // l'onboarding express bascule PostGalleryGrid sur le skeleton → démonte SetupExpress
  // → son state local `phase` est perdu et la modale réapparaît en `intro`. Défaut =
  // non-silencieux (comportement historique : skeleton au 1er chargement).
  const refreshData = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!currentSpace) {
      if (!silent) setIsDataLoading(false);
      return;
    }

    if (!silent) setIsDataLoading(true);
    try {
      // First, check if server is available
      try {
        const healthResponse = await dataService.checkHealth();
        
        if (!healthResponse.ok) {
          Logger.log('Server health check failed, using mock data');
          return;
        }
        
        Logger.log('Server health check passed');
      } catch (error) {
        Logger.log('Server is not available yet, using mock data. This is normal if the Edge Function is not deployed.');
        return;
      }

      // Load communication channels
      // V4-GUEST-DASHBOARD-FETCH-403 — les guests (compte invité, read-only) reçoivent
      // 403 sur channels/sources/automation → toast global "Accès Refusé" trompeur.
      // Un guest n'a besoin que des posts ; on skip ces fetches pour eux.
      if (!isGuest) try {
        const channelsData = await channelsService.getAll(currentSpace.id);
        if (channelsData && channelsData.length > 0) {
          Logger.log('Loaded communication channels from database:', channelsData);
          setCommunicationChannels(channelsData);
        } else {
          Logger.log('No communication channels in database, using mock data');
        }
      } catch (error) {
        Logger.log('ℹ️ Using mock communication channels - server not ready yet');
      }

      // Load posts
      try {
        const postsData = await postsService.getAll(currentSpace.id);
        if (postsData && postsData.length > 0) {
          Logger.log('Loaded posts from database:', postsData.length);
          applyLoadedPosts(postsData);
        } else {
          Logger.log('No posts in database');
          applyLoadedPosts([]);
        }
      } catch (error) {
        // Même fallback silencieux que les channels ci-dessus (ligne 205) : ce n'est pas une
        // vraie erreur affichée à l'utilisateur, donc pas de `Logger.error` (bruyant en prod,
        // et détecté comme "console.error inattendu" par les specs Playwright — cf. 15:78).
        Logger.log('ℹ️ Failed to load posts - server might not be ready');
      }

      // Load sources (skip pour les guests — cf. V4-GUEST-DASHBOARD-FETCH-403)
      if (!isGuest) try {
        const sourcesData = await sourcesService.getAll(currentSpace.id);
        if (sourcesData) {
          // Fix V4-UI-DEDUP (2026-05-21) : dédup défensive même au chargement
          // initial, au cas où le backend renverrait des doublons (bug ES sync).
          const seen = new Set<string>();
          const deduped = sourcesData.filter(s => {
            const key = s.id || s.url || s.title;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setSources(deduped);
        }
      } catch (error) {
        Logger.error('Error loading sources:', error);
      }

      // Load watch topics
      try {
        const watchTopicsData = await watchTopicsService.getAll(currentSpace.id);
        if (watchTopicsData) {
          setWatchTopics(watchTopicsData);
        }
      } catch (error) {
        Logger.error('Error loading watch topics:', error);
      }

      // Load positioning — fix V4-AGENCY-3 : passer space_id pour éviter
      // que le backend retombe sur "first owned space" (data leak entre spaces
      // d'une agence multi-tenant).
      try {
        await fetchPositioning(currentSpace.id);
      } catch (error) {
        Logger.error('Error loading positioning:', error);
      }

      // Load automation settings
      try {
        await fetchAutomationSettings(currentSpace.id);
      } catch (error) {
        Logger.error('Error loading automation settings:', error);
      }
    } catch (error) {
      Logger.error('ℹ️ Using mock data - backend server will be available after deployment');
    } finally {
      if (!silent) setIsDataLoading(false);
    }
  };

  // Load data from Supabase on mount
  useEffect(() => {
    refreshData();
  }, [currentSpace]);

  return {
    posts, setPosts,
    queue, setQueue,
    allDatedPosts,
    newsletterPosts, setNewsletterPosts,
    sources, setSources,
    watchTopics, setWatchTopics,
    communicationChannels, setCommunicationChannels,
    droppedPostForScheduling, setDroppedPostForScheduling,
    automationSettings, setAutomationSettings,
    systemPrompt, setSystemPrompt,
    isPositioningTemplate,
    sectorWatchEnabled,
    logoUrl, setLogoUrl,
    newsletterTemplate, setNewsletterTemplate,
    isGenerateModalOpen, setIsGenerateModalOpen,
    isDataLoading,
    
    refreshData,
    refreshPosts,
    refreshSources,
    addToQueue: addToQueueAndUpdateCalendar,
    updatePost: updatePostAndSyncCalendar,
    unschedulePost: unschedulePostAndSyncCalendar,
    deletePost: deletePostAndSyncCalendar,
    generateNewPosts,
    handleCreatePost,
    duplicatePost,
    createPost,
    addSource,
    updateSource,
    deleteSource,
    enrichingTopicIds,
    addWatchTopic,
    updateWatchTopic,
    deleteWatchTopic,
    addCommunicationChannel,
    updateCommunicationChannel,
    deleteCommunicationChannel,
    loadChannels,
    addToNewsletter,
    removeFromNewsletter,
    scheduleNewsletter,
    fetchAutomationSettings,
    updateAutomationSettings,
    publishPostNow: publishPostNowAndSyncCalendar
  };
}
